import React, { useState, useEffect } from "react";
import { useFetcher } from "../../../../../hooks/fetcher";
import { RecentDepositTableSection } from "../../styles";
import OverviewRightTableList from "./OverviewRightTableList";
import { View, Text } from "@/styles/reactnative";
import { COLORS } from "@app/constants";
import { useTranslation } from "react-i18next";
import { useIsMobile, MobileCalVW } from "@utils/CalVW.tsx";
import EmptyMessage from "@/app/components/EmptyMessage";

const RecentDepositTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const { data: res, refresh } = useFetcher(
    `/cash/list?type=*&page=${currentPage}&maxData=${itemsPerPage}`,
    true,
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
    }
  );
  const data = res?.result?.result?.list;
  
  const totalCount = 
    res?.result?.result?.totalCount || 
    res?.result?.total || 
    res?.total || 
    (data?.length >= itemsPerPage ? (currentPage * itemsPerPage) : data?.length) || 
    0;

  useEffect(() => {
    // 실제 총 데이터터 수를 계산하기 위한 조건 추가
    if (totalCount > 0) {
      const calculatedPages = Math.ceil(totalCount / itemsPerPage);
      console.log("총 항목 수:", totalCount, "계산된 페이지 수:", calculatedPages);
      
      // API에서 제공하는 totalCount를 사용하여 페이지 수 설정
      setTotalPages(calculatedPages);
    } else if (data?.length === itemsPerPage) {
      // 데이터가 itemsPerPage와 같으면 다음 페이지가 있을 가능성이 높음
      setTotalPages(currentPage + 1);
    } else if (data?.length > 0) {
      // 데이터가 있지만 itemsPerPage보다 적으면 현재 페이지가 마지막 페이지
      setTotalPages(currentPage);
    } else {
      // 데이터가 없으면 페이지를 1로 설정
      setTotalPages(1);
    }
  }, [res, totalCount, data, currentPage, itemsPerPage]);

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPagination = () => {
    const pageNumbers: number[] = [];
    const maxPageButtons = isMobile ? 3 : 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <View
        style={{
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          marginTop: isMobile ? MobileCalVW(15) : 15,
          gap: isMobile ? MobileCalVW(5) : 10
        }}
      >
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          style={{
            opacity: currentPage === 1 ? 0.5 : 1,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: COLORS.lightGray,
            fontWeight: "bold"
          }}
        >
          {"<<"}
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            opacity: currentPage === 1 ? 0.5 : 1,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: COLORS.lightGray,
            fontWeight: "bold"
          }}
        >
          {"<"}
        </button>
        
        {pageNumbers.map((number) => (
          <button
            key={number}
            onClick={() => handlePageChange(number)}
            style={{
              backgroundColor: currentPage === number ? COLORS.primary : "transparent",
              padding: isMobile ? `${MobileCalVW(5)}px ${MobileCalVW(10)}px` : "5px 10px",
              borderRadius: "5px",
              border: "none",
              cursor: "pointer",
              color: currentPage === number ? "white" : COLORS.lightGray,
              fontWeight: "500"
            }}
          >
            {number}
          </button>
        ))}
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            opacity: currentPage === totalPages ? 0.5 : 1,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: COLORS.lightGray,
            fontWeight: "bold"
          }}
        >
          {">"}
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          style={{
            opacity: currentPage === totalPages ? 0.5 : 1,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: COLORS.lightGray,
            fontWeight: "bold"
          }}
        >
          {">>"}
        </button>
      </View>
    );
  };

  return (
    <View>
      <RecentDepositTableSection>
        {data?.length > 0 ? (
          data.map((item: any, index: number) => (
            <OverviewRightTableList key={index} data={item} />
          ))
        ) : (
          <EmptyMessage />
        )}
      </RecentDepositTableSection>
      {totalPages > 1 && renderPagination()}
    </View>
  );
};

export default React.memo(RecentDepositTable);
